import { useEffect, useState } from "react"
import { useFormikContext } from "formik"
import { makeStyles } from "tss-react/mui"
import { Stack } from "@mui/material"
import { MeetingServiceConfig, DynamicConfigKey, MeetingServiceTabValues } from "../form/formTypes"
import { MeetingServiceTimeErrorDialog } from "./orderSummary/MeetingServiceTimeErrorDialog"
import { DimensionsGuardManager } from "./fieldGuards/DimensionsGuardManager"
import { ConfirmOrderSubmitButton } from "../ConfirmOrderSubmitButton"
import { BillReceiverGuard } from "./fieldGuards/BillReceiverGuard"
import { CreateOrderDialogMode, CreateOrderDialogType } from "../../createOrderTypes"
import { SelectedMainUserState } from "../../createOrderTypes"
import { INVALID_DELIVERIES } from "../form/helpers"
import { DrawerSections } from "./DrawerSections"
import { CreateOrderType } from "../../helpers"
import { Messages } from "./messages/Messages"

const defaultDialogState = {
    show: false,
    times: [],
}

const useStyles = makeStyles()(() => ({
    container: {
        overflow: "auto",
    },
}))

type DrawerContentProps = Readonly<{
    meetingServiceConfiguration: MeetingServiceConfig
    dialogType: CreateOrderDialogType
    user: SelectedMainUserState
    orderType: CreateOrderType
    mode: CreateOrderDialogMode
}>

export function DrawerContent({ meetingServiceConfiguration, dialogType, user, orderType, mode }: DrawerContentProps) {
    const { classes } = useStyles()
    const { sections } = meetingServiceConfiguration
    const { values, errors, setFieldValue, isSubmitting } = useFormikContext<MeetingServiceTabValues>()

    const [expandedAccordionKey, setExpandedAccordionKey] = useState<string | null>("section-0")
    const [showInvalidTimesModal, setShowInvalidTimesModal] = useState<{ show: boolean; times: string[] }>(
        defaultDialogState,
    )

    useEffect(() => {
        if (isSubmitting && Object.keys(errors).length > 0) {
            const errorKeys = Object.keys(errors)

            if (errorKeys.includes(DynamicConfigKey.MULTIPLE_DELIVERIES)) {
                const invalidTimes = errors[DynamicConfigKey.MULTIPLE_DELIVERIES]
                if (typeof invalidTimes === "string" && invalidTimes.startsWith(INVALID_DELIVERIES)) {
                    const times = invalidTimes.replace(INVALID_DELIVERIES, "").split(",") || []
                    if (times.length > 0) {
                        setShowInvalidTimesModal({
                            show: true,
                            times,
                        })
                    }
                }
                return
            }

            for (let i = 0; i < sections.length; i++) {
                if (sections[i].groups.some((g) => g.fields.some((f) => errorKeys.includes(f.key)))) {
                    setExpandedAccordionKey(`section-${i}`)
                    break
                }
            }
        }
    }, [isSubmitting, errors, sections])

    const handleDialogClose = () => {
        if (showInvalidTimesModal.times.length > 0) {
            const firstInvalidTime = showInvalidTimesModal.times[0]
            setFieldValue(`${DynamicConfigKey.MULTIPLE_DELIVERIES}.activeTime`, firstInvalidTime)
        }
        setShowInvalidTimesModal(defaultDialogState)
    }

    return (
        <>
            <Stack gap=".75rem" className={classes.container}>
                {!!values["priceList"] && <Messages />}
                <DrawerSections
                    meetingServiceConfiguration={meetingServiceConfiguration}
                    expandedAccordionKey={expandedAccordionKey}
                    setExpandedAccordionKey={setExpandedAccordionKey}
                    dialogType={dialogType}
                    user={user}
                    orderType={orderType}
                />
            </Stack>
            <ConfirmOrderSubmitButton isRefundOrder={dialogType === CreateOrderDialogType.CREDIT_NOTE} />
            {showInvalidTimesModal.show && (
                <MeetingServiceTimeErrorDialog
                    show={showInvalidTimesModal.show}
                    times={showInvalidTimesModal.times}
                    handleDialogClose={handleDialogClose}
                />
            )}

            {mode !== "create" && (
                <>
                    <DimensionsGuardManager userId={user.userId} />
                    <BillReceiverGuard userId={user.userId} />
                </>
            )}
        </>
    )
}
